/**
 * What a draft is CALLED, in one place.
 *
 * The draft picker in the review drawer said it — `name.replace(/^draft\//, '')`
 * plus a word for a finished one — and it was the only thing that did, because
 * it was the only surface that named a draft at all. That was the defect: a
 * writer looking at the editor could not see which draft they were on, or that
 * they were on one, so the top bar read as though every keystroke went to the
 * live site. The invisible-draft model exists so nobody has to answer a
 * branching question before typing; it was never meant to hide the answer.
 *
 * Shared rather than copied because two surfaces naming one thing differently
 * is how a person ends up believing there are two things.
 */
export interface DraftFacts {
  name: string
  status?: string | null
}

/** The `draft/` prefix is bookkeeping, not a name. */
export function draftLabel(branch: string | null, info?: DraftFacts | null): string {
  if (!branch) return 'Live site'
  const bare = branch.replace(/^draft\//, '')
  if (info?.status === 'merged') return `${bare} — published`
  if (info?.status === 'closed') return `${bare} — closed`
  return bare
}

/**
 * The second half of the chip, and the half that answers the question.
 *
 * "Draft" or "Read only" rather than the draft's name: somebody who cannot
 * tell whether their work is live is asking which of the two they are in, and
 * a name alone does not answer it.
 */
export function draftKind(branch: string | null): 'Draft' | 'Read only' {
  return branch ? 'Draft' : 'Read only'
}

/**
 * What the chip says when somebody rests on it — including the way out, since
 * a control that explains a state and offers nothing is a label.
 */
export function draftTip(branch: string | null, staged: number): string {
  if (!branch) {
    return 'You are reading the live site. Editing anything starts a draft; nothing you do here is published until you publish it.'
  }
  const work =
    staged === 0
      ? 'Nothing is staged on it yet'
      : `${staged} change${staged === 1 ? '' : 's'} staged`
  return `Your work is on this draft, not on the live site. ${work} — open the review to see it.`
}
